import List from '@pelckmans/elementis/components/list';
import ListGroup from '@pelckmans/elementis/components/list-group';
import SelectField from '@pelckmans/elementis/components/select-field';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MediaContentType } from '../../../../api/MediaContentTypeApi';
import { Module } from '../../../../api/module';
import ModuleStatus from '../../../../components/ModuleStatus';
import LoadingState from '../../../../shared-components/LoadingState';
import useModuleAccess from '../../../../hooks/useModuleAccess';
import ModuleContext from '../../_contexts/ModuleContext';
import { CategoricalContent, CategoricalContentTypeContext } from '../context/CategoricalContentTypeContext';
import { QueryMaterialContext, SEARCH_PARAMS } from '../context/QueryMaterialContext';
import { flattenCategoricalMaterialPerModule } from '../utils/categoricalMaterialUtils';
import { countContentType, countUsedContentByCategory } from '../utils/sidebarFiltersUtils';

const BASE = 'PAGES.CATEGORICAL.BROWSE_MODE.ASIDE.FILTERS';

const getListWithToggledId = (selectedModuleIds: string[], toggledModuleId: string) =>
  selectedModuleIds.includes(toggledModuleId)
    ? selectedModuleIds.filter(x => x !== toggledModuleId)
    : [...selectedModuleIds, toggledModuleId];

const ModuleItem = ({ module }: { module: Module }) => {
  const { setSearchParam } = useContext(QueryMaterialContext);
  const { categoricalContentPerModule } = useContext(CategoricalContentTypeContext);
  const { materialCategory } = useParams();
  const { canOpen } = useModuleAccess(module);
  const [searchParams] = useSearchParams();

  const contentTypeIds = useMemo(() => searchParams.get(SEARCH_PARAMS.CONTENT_TYPE_IDS) || undefined, [searchParams]);
  const selectedModuleIds = useMemo(
    () => searchParams.get(SEARCH_PARAMS.SELECTED_MODULE_IDS)?.split(',') || [],
    [searchParams],
  );

  const count = countUsedContentByCategory({
    categoricalContent: categoricalContentPerModule[module.id],
    materialCategory,
    contentTypeIds,
  });

  const toggleModuleItem = (moduleId: string) => {
    setSearchParam({
      [SEARCH_PARAMS.SELECTED_MODULE_IDS]: getListWithToggledId(selectedModuleIds, moduleId).join(','),
    });
  };

  return (
    <List.Item
      title={`${module.niceName} (${count})`}
      as="checkbox"
      checked={selectedModuleIds.includes(module.id)}
      onClick={() => toggleModuleItem(module.id)}
      disabled={!canOpen}
      status={<ModuleStatus module={module} showUnlicensedStatus />}
    />
  );
};

const ContentTypeItem = ({
  contentType,
  category,
}: {
  contentType: MediaContentType;
  category: CategoricalContent;
}) => {
  const { modulesForResults, setSearchParam } = useContext(QueryMaterialContext);
  const { categoricalContentPerModule } = useContext(CategoricalContentTypeContext);
  const [searchParams] = useSearchParams();

  const contentTypeIds = useMemo(() => searchParams.get(SEARCH_PARAMS.CONTENT_TYPE_IDS) || undefined, [searchParams]);

  const contentTypesByModules = useMemo(() => {
    if (!category?.name) return [];

    const relevantCategories = modulesForResults.flatMap(moduleId => categoricalContentPerModule[moduleId] ?? []);

    const matchingCategories = relevantCategories.filter(item => item.name === category.name);

    return matchingCategories.flatMap(item => item.contentTypes);
  }, [categoricalContentPerModule, category?.name, modulesForResults]);

  const getUpdatedContentTypeIds = (contentTypeIds: string, contentTypeId: string) => {
    const list = getListWithToggledId(contentTypeIds ? contentTypeIds.split(',') : [], contentTypeId).join(',');
    return list === '' ? undefined : list;
  };

  const toggleContentType = (contentTypeId: string) => {
    setSearchParam({ [SEARCH_PARAMS.CONTENT_TYPE_IDS]: getUpdatedContentTypeIds(contentTypeIds || '', contentTypeId) });
  };

  const count = countContentType(contentTypesByModules, contentType.id);

  return (
    <List.Item
      title={`${contentType.name} (${count})`}
      as="checkbox"
      checked={Boolean(contentTypeIds?.includes(contentType.id))}
      onClick={() => toggleContentType(contentType.id)}
    />
  );
};

export default function SidebarFilters() {
  const { t } = useTranslation();
  const { allModules: modules } = useContext(ModuleContext);
  const { categoricalContentPerModule, loading } = useContext(CategoricalContentTypeContext);
  const navigate = useNavigate();
  const params = useParams();
  const categoricalContent = flattenCategoricalMaterialPerModule(categoricalContentPerModule);
  const category = categoricalContent.find(category => category.name === params.materialCategory);

  function updateCategoryNameValue(categoryName: string) {
    navigate(`../${encodeURIComponent(categoryName)}`, { relative: 'path' });
  }

  if (!category && categoricalContent.length !== 0) return <Navigate to="." />;

  const categoryName = decodeURIComponent(params.materialCategory || '');

  return (
    <div className="sidebar-filters">
      <SelectField
        value={categoryName}
        onChange={updateCategoryNameValue}
        renderValueContent={c => c as string}
        required
        labelText={t(`${BASE}.CATEGORY.A11Y_LABEL`)}
        dropdownMenuProps={{
          ariaLabel: t(`${BASE}.CATEGORY.DROPDOWN_MENU.A11Y_LABEL`),
        }}
      >
        {categoricalContent.map(c => (
          <SelectField.Item value={c.name} text={c.name} key={c.name} selected={c.name === categoryName} />
        ))}
      </SelectField>
      <ListGroup>
        <List density="compact" showSeparators={false}>
          <List.Header title={t(`${BASE}.MODULE.TITLE`)} titleTag="h3" />
          {loading ? (
            <LoadingState
              variant="list"
              aria-label={t(`${BASE}.MODULE.LOADING_STATE.A11Y_LABEL`)}
              size={4}
              height={40}
            />
          ) : (
            modules.map(module => <ModuleItem module={module} key={module.id} />)
          )}
        </List>
        <List density="compact" showSeparators={false}>
          <List.Header title={t(`${BASE}.CONTENT_TYPE.TITLE`)} titleTag="h3" />
          {loading ? (
            <LoadingState variant="list" aria-label={t(`${BASE}.CONTENT_TYPE.LOADING_STATE.A11Y_LABEL`)} height={40} />
          ) : (
            category!.contentTypes.map(contentType => (
              <ContentTypeItem contentType={contentType} key={contentType.id} category={category!} />
            ))
          )}
        </List>
      </ListGroup>
    </div>
  );
}
